import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';

const STORAGE_KEY = '5dbs.cookie-consent.v1';

const CookieBanner = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (!saved) {
        // Small delay so banner slides in after page paint.
        const t = setTimeout(() => setVisible(true), 600);
        return () => clearTimeout(t);
      }
    } catch (e) {
      // localStorage unavailable — show once, don't persist.
      setVisible(true);
    }
  }, []);

  const persist = (choice) => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ choice, ts: new Date().toISOString() }),
      );
    } catch (e) {
      /* noop */
    }
    // Notify Google Consent Mode of the visitor's decision.
    try {
      const granted = choice === 'accepted';
      const state = granted ? 'granted' : 'denied';
      if (typeof window.gtag === 'function') {
        window.gtag('consent', 'update', {
          ad_storage: state,
          analytics_storage: state,
          ad_user_data: state,
          ad_personalization: state,
        });
      }
    } catch (e) {
      /* noop */
    }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
      data-testid="cookie-banner"
      className="fixed inset-x-3 bottom-3 md:inset-x-auto md:left-6 md:bottom-6 md:max-w-md z-[80] animate-in fade-in slide-in-from-bottom-4 duration-500"
    >
      <div className="relative rounded-2xl bg-navy-deep/95 backdrop-blur-md shadow-mk-lg border border-white/10 p-5 md:p-6 text-white">
        <button
          type="button"
          aria-label="Dismiss cookie notice"
          data-testid="cookie-banner-close"
          onClick={() => persist('dismissed')}
          className="absolute top-2.5 right-2.5 h-8 w-8 inline-flex items-center justify-center rounded-full text-white/60 hover:text-white hover:bg-white/10 transition-colors"
        >
          <X className="h-4 w-4" />
        </button>

        <p className="text-white text-[14px] font-bold tracking-[-0.005em]">
          We value your privacy
        </p>
        <p className="mt-2 text-white/75 text-[13px] leading-[1.55]">
          5DBS uses essential cookies to run this site and optional analytics cookies to
          understand how visitors engage with our content. You can opt out any time. See our{' '}
          <Link
            to="/privacy"
            className="text-brand-pink font-semibold hover:underline"
            data-testid="cookie-banner-privacy-link"
          >
            Privacy Policy
          </Link>
          .
        </p>

        <div className="mt-4 flex flex-wrap items-center gap-2.5">
          <button
            type="button"
            data-testid="cookie-banner-accept-all"
            onClick={() => persist('accepted')}
            className="inline-flex items-center justify-center h-10 px-5 rounded-md bg-brand-pink hover:bg-brand-pink/90 text-white text-[12px] font-bold uppercase tracking-[0.08em] shadow-mk hover:shadow-mk-lg hover:-translate-y-0.5 transition-all duration-300"
          >
            Accept all
          </button>
          <button
            type="button"
            data-testid="cookie-banner-essential-only"
            onClick={() => persist('essential-only')}
            className="inline-flex items-center justify-center h-10 px-5 rounded-md bg-transparent border border-white/25 hover:border-white/60 text-white text-[12px] font-bold uppercase tracking-[0.08em] transition-colors"
          >
            Essential only
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
